/**
 * Hook useContactsUnreadSummary - Busca resumo de mensagens não lidas por contato
 * 
 * Chama a RPC batch `get_contacts_unread_summary` para obter:
 * - unreadCount: total de mensagens não lidas (filtrado por inboxes acessíveis)
 * - lastConversationId: ID da conversa mais recente do contato
 * 
 * Versão: 1.0.0 - 2026-03-07
 */
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { createLogger } from '@/lib/logger';

// Logger condicional do módulo
const logger = createLogger('ContactsUnreadSummary');

// Tipo do item retornado pela RPC
interface UnreadSummaryItem {
  contact_id: string;
  unread_count: number;
  last_conversation_id: string | null;
}

// Tipo do valor no Map retornado pelo hook
export interface UnreadSummaryData {
  unreadCount: number;
  lastConversationId: string | null;
}

/**
 * Hook batch para buscar resumo de não lidas de múltiplos contatos.
 * Retorna um Map<contactId, UnreadSummaryData> para lookup O(1) no DealCard.
 * 
 * @param contactIds - Array de contact_ids dos deals visíveis
 * @param enabled - Se deve executar a query (default: true)
 */
export function useContactsUnreadSummary(
  contactIds: (string | null)[],
  enabled: boolean = true
) {
  const { currentTenant } = useAuth();

  // Filtra nulls e deduplica IDs para query key estável
  const validIds = useMemo(() => {
    const unique = [...new Set(contactIds.filter((id): id is string => !!id))];
    unique.sort(); // Ordena para cache key consistente
    return unique;
  }, [contactIds]);

  // Chave estável para React Query
  const queryKey = useMemo(
    () => ['contacts-unread-summary', validIds.join(',')],
    [validIds]
  );

  const query = useQuery({
    queryKey,
    queryFn: async () => {
      if (validIds.length === 0) return new Map<string, UnreadSummaryData>();

      logger.debug(`Buscando resumo de não lidas para ${validIds.length} contatos`);

      // Chamada RPC com cast para unknown (função não tipada no types.ts)
      const { data, error } = await (supabase.rpc as any)('get_contacts_unread_summary', {
        p_contact_ids: validIds,
        p_tenant_hint: currentTenant?.id || null,
      });

      if (error) {
        logger.error('Erro ao buscar resumo de não lidas:', error);
        throw error;
      }

      // Converte array para Map para lookup O(1)
      const result = new Map<string, UnreadSummaryData>();
      ((data || []) as UnreadSummaryItem[]).forEach((item) => {
        result.set(item.contact_id, {
          unreadCount: item.unread_count,
          lastConversationId: item.last_conversation_id,
        });
      });

      logger.debug(`Resumo: ${result.size} contatos com conversas`);
      return result;
    },
    enabled: enabled && validIds.length > 0 && !!currentTenant?.id,
    staleTime: 30 * 1000, // 30 segundos
    gcTime: 5 * 60 * 1000, // 5 minutos
  });

  return {
    /** Map de contactId → { unreadCount, lastConversationId } */
    summaryMap: query.data || new Map<string, UnreadSummaryData>(),
    isLoading: query.isLoading,
    error: query.error,
  };
}
